import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { postApi } from '../services/apiServices';
import { toast } from 'react-toastify';

const ContactUs = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile_number: '',
        topic: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(true);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateMobile = (mobile) => {
        const mobileRegex = /^\+?[\d\s\-\(\)]{10,}$/;
        return mobileRegex.test(mobile);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('contact.form.validation.nameRequired');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('contact.form.validation.nameMinLength');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('contact.form.validation.emailRequired');
        } else if (!validateEmail(formData.email)) {
            newErrors.email = t('contact.form.validation.emailInvalid');
        }

        if (!formData.mobile_number.trim()) {
            newErrors.mobile_number = t('contact.form.validation.mobileRequired');
        } else if (!validateMobile(formData.mobile_number)) {
            newErrors.mobile_number = t('contact.form.validation.mobileInvalid');
        }

        if (!formData.topic) {
            newErrors.topic = t('contact.form.validation.topicRequired');
        }

        if (!formData.message.trim()) {
            newErrors.message = t('contact.form.validation.messageRequired');
        } else if (formData.message.trim().length < 10) {
            newErrors.message = t('contact.form.validation.messageMinLength');
        }

        if (!termsAccepted) {
            newErrors.terms = t('contact.form.validation.termsRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        
        if (id === 'mobile_number') {
          
            const hasAlphabets = /[a-zA-Z]/.test(value);
            if (hasAlphabets) {
                return; 
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));

        if (errors[id]) {
            setErrors(prev => ({
                ...prev,
                [id]: ''
            }));
        }
    };

    const handleKeyDown = (e) => {
        const { id, key } = e.target;
        
        if (id === 'mobile_number') {
          
            const isAlphabet = /^[a-zA-Z]$/.test(key);
            if (isAlphabet) {
                e.preventDefault(); 
            }
        }
    };

    const handleTermsChange = (e) => {
        setTermsAccepted(e.target.checked);
        if (errors.terms) {
            setErrors(prev => ({
                ...prev,
                terms: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error(t('contact.form.validation.formErrors'));
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                mobile_number: formData.mobile_number.trim(),
                topic: formData.topic,
                message: formData.message.trim()
            };

            const response = await postApi('/api/contact-us', payload, { showSuccessToast: false });
            
            if (response?.status) {
                toast.success(response?.message || t('contact.form.success'));
                
                
                setFormData({
                    name: '',
                    email: '',
                    mobile_number: '',
                    topic: '',
                    message: ''
                });
                setErrors({});
            } else {
                toast.error(response?.message || t('contact.form.error'));
            }
        } catch (error) {
            console.error('Contact form submission error:', error);
            
            let errorMessage = t('contact.form.error');
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* ── Page hero ───────────────────────────────────────────── */}
            <section className="what-Offer blog speed-policy contact">
                <div className="container">
                    <div className="contact-left">
                        <p className="r4l-kicker">{t('r4l.pages.contactKicker')}</p>
                        <h3>{t('contact.hero.title')}</h3>
                        <p>{t('contact.hero.sub')}</p>
                    </div>
                </div>
            </section>

            {/* ── Support channels ─────────────────────────────────────── */}
            <section className="r4l-contact-channels">
                <div className="r4l-wrap">
                    <div className="r4l-channel-grid">

                        {/* Live Support (Redirects to WhatsApp) */}
                        <div className="r4l-channel-card r4l-channel-live">
                            <div className="r4l-channel-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                                </svg>
                            </div>
                            <div className="r4l-channel-body">
                                <h3>{t('contact.cards.live.title')}</h3>
                                <p>{t('contact.cards.live.desc')}</p>
                                <div className="r4l-channel-hours">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="r4l-hours-icon" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10"/>
                                        <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                    <span>{t('contact.cards.live.hours', 'Support hours: 9:00 AM – 6:00 PM')}</span>
                                </div>
                                <a
                                    href={`https://wa.me/${t('contact.whatsapp.number').replace(/[^\d]/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="r4l-channel-cta"
                                    id="contact-start-chat-btn"
                                >
                                    {t('contact.cards.live.cta')}
                                    <span className="r4l-channel-arrow" aria-hidden="true">→</span>
                                </a>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="r4l-channel-card r4l-channel-email">
                            <div className="r4l-channel-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <rect x="2" y="4" width="20" height="16" rx="3"/>
                                    <polyline points="2,4 12,13 22,4"/>
                                </svg>
                            </div>
                            <div className="r4l-channel-body">
                                <h3>{t('contact.cards.email.title')}</h3>
                                <p>{t('contact.cards.email.desc')}</p>
                                <a
                                    href={`mailto:${t('contact.email.address')}`}
                                    className="r4l-channel-cta"
                                >
                                    {t('contact.cards.email.cta')}
                                    <span className="r4l-channel-arrow" aria-hidden="true">→</span>
                                </a>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── Contact form ─────────────────────────────────────────── */}
            <section className="contact-form">
                <div className="container">
                    <div className="contact-form-main">
                        <h3>
                            <span dangerouslySetInnerHTML={{ __html: t('contact.form.title') }} />
                        </h3>
                        <p>
                            <span dangerouslySetInnerHTML={{ __html: t('contact.form.description') }} />
                        </p>
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="name">{t('contact.form.fields.name')} *</label>
                                        <input 
                                            type="text" 
                                            className={`form-control ${errors.name ? 'error' : ''}`}
                                            id="name" 
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.form.placeholders.name')}
                                        />
                                        {errors.name && <span className="error-message">{errors.name}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="email">{t('contact.form.fields.email')} *</label>
                                        <input 
                                            type="email" 
                                            className={`form-control ${errors.email ? 'error' : ''}`}
                                            id="email" 
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.form.placeholders.email')}
                                        />
                                        {errors.email && <span className="error-message">{errors.email}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="mobile_number">{t('contact.form.fields.mobileNumber')} *</label>
                                        <input 
                                            type="text" 
                                            className={`form-control ${errors.mobile_number ? 'error' : ''}`}
                                            id="mobile_number" 
                                            value={formData.mobile_number}
                                            onChange={handleInputChange}
                                            onKeyDown={handleKeyDown}
                                            placeholder={t('contact.form.placeholders.mobileNumber')}
                                        />
                                        {errors.mobile_number && <span className="error-message">{errors.mobile_number}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="topic">{t('contact.form.fields.topic')} *</label>
                                        <select 
                                            className={`form-control ${errors.topic ? 'error' : ''}`}
                                            id="topic"
                                            value={formData.topic}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">{t('contact.form.placeholders.topic')}</option>
                                            <option value="activation-problem">{t('contact.form.topics.activationProblem')}</option>
                                            <option value="login-issue">{t('contact.form.topics.loginIssue')}</option>
                                            <option value="payment-failure">{t('contact.form.topics.paymentFailure')}</option>
                                            <option value="bug-report">{t('contact.form.topics.bugReport')}</option>
                                            <option value="other">{t('contact.form.topics.other')}</option>
                                        </select>
                                        {errors.topic && <span className="error-message">{errors.topic}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label htmlFor="message">{t('contact.form.fields.message')} *</label>
                                        <textarea 
                                            className={`form-control message ${errors.message ? 'error' : ''}`}
                                            id="message" 
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.form.placeholders.message')}
                                            rows="5"
                                        ></textarea>
                                        {errors.message && <span className="error-message">{errors.message}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <label className="term">
                                        <Trans i18nKey="contact.form.terms" components={[
                                            <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" />,
                                            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" />
                                        ]} />
                                        <input 
                                            type="checkbox" 
                                            checked={termsAccepted}
                                            onChange={handleTermsChange}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                    {errors.terms && <span className="error-message">{errors.terms}</span>}
                                </div>
                                <div className="col-sm-12">
                                    <button 
                                        type="submit" 
                                        className="send-message" 
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? t('contact.form.sendingButton') : t('contact.form.sendButton')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ContactUs;
